<template>
  <div class="profile-container">
    <div v-if="loading" class="loading">Loading...</div>

    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 text-red-600 dark:text-red-400"
    >
      {{ error }}
    </div>

    <div v-else class="profile-content">
      <!-- Header with photo -->
      <div class="profile-header">
        <div class="profile-photo-container">
          <img
            v-if="userProfile.profile?.photoURL && !showFallbackAvatar"
            ref="profileImage"
            :src="userProfile.profile.photoURL"
            alt="Profile"
            class="profile-photo"
            @error="handleImageError"
          />
          <div
            v-if="!userProfile.profile?.photoURL || showFallbackAvatar"
            class="fallback-avatar"
          >
            {{ getInitials(userProfile.profile?.displayName) }}
          </div>
        </div>
        <div class="profile-title">
          <h1>
            {{ userProfile.profile?.displayName || "User Profile" }}
          </h1>
          <p class="email">{{ userProfile.email }}</p>
        </div>
      </div>

      <!-- Edit form -->
      <div v-if="isEditing">
        <div class="form-group">
          <label for="displayName">Display Name</label>
          <input
            id="displayName"
            v-model="editForm.displayName"
            type="text"
            class="form-input"
          />
        </div>
        <div class="form-group">
          <label for="phoneNumber">Phone Number</label>
          <input
            id="phoneNumber"
            :value="editForm.phoneNumber"
            type="tel"
            placeholder="+1234567890"
            class="form-input"
            :class="{ 'error-input': phoneError }"
            @input="handlePhoneInput"
          />
          <p v-if="phoneError" class="error-text">{{ phoneError }}</p>
          <p class="help-text">
            Format: +[country code][number], e.g., +12125551234
          </p>
        </div>
        <div class="button-group">
          <button class="btn-primary" @click="saveProfile">Save Changes</button>
          <button class="btn-secondary" @click="cancelEdit">Cancel</button>
        </div>
      </div>

      <!-- Profile information -->
      <div v-else class="profile-info">
        <div class="info-section">
          <h2>Account Information</h2>
          <div class="info-grid">
            <div class="info-row">
              <label>User ID</label>
              <span>{{ userProfile.uid }}</span>
            </div>
            <div class="info-row">
              <label>Email</label>
              <span>{{ userProfile.email }}</span>
            </div>
            <div class="info-row">
              <label>SSO Provider</label>
              <span>{{
                userProfile.profile?.providerId?.replace(".com", "")
              }}</span>
            </div>
          </div>
        </div>

        <div class="info-section">
          <h2>Profile Details</h2>
          <div class="info-grid">
            <div class="info-row">
              <label>Display Name</label>
              <span>{{ userProfile.profile?.displayName }}</span>
            </div>
            <div class="info-row">
              <label>Phone Number</label>
              <span>{{ userProfile.profile?.phoneNumber || "Not set" }}</span>
            </div>
            <div class="info-row">
              <label>Last Sign In</label>
              <span>{{ formatDate(userProfile.profile?.lastSignInTime) }}</span>
            </div>
          </div>
        </div>

        <button class="btn-primary mt-6" @click="startEdit">
          Edit Profile
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import kappamlAPI from "@/services/kappaml-api";

export default {
  name: "Profile",
  data() {
    return {
      userProfile: null,
      loading: true,
      error: null,
      isEditing: false,
      editForm: {
        displayName: "",
        phoneNumber: "",
      },
      showFallbackAvatar: false,
      phoneError: null,
    };
  },
  async created() {
    this.fetchUserProfile();
  },
  methods: {
    async fetchUserProfile() {
      try {
        this.userProfile = await kappamlAPI.getUserProfile();
        this.error = null;
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    startEdit() {
      this.editForm.displayName = this.userProfile.profile?.displayName || "";
      this.editForm.phoneNumber = this.userProfile.profile?.phoneNumber || "";
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editForm.displayName = "";
      this.editForm.phoneNumber = "";
    },
    validatePhoneNumber(number) {
      // Remove all non-digit characters except leading +
      const cleaned = number.replace(/[^\d+]/g, "");

      // Check if it matches E.164 format: +1234567890 (1-15 digits)
      const e164Regex = /^\+?[1-9]\d{1,14}$/;
      return e164Regex.test(cleaned) ? cleaned : null;
    },
    handlePhoneInput(e) {
      let value = e.target.value;

      // Allow only digits, +, spaces, and parentheses while typing
      if (!/^[0-9+\s()]*$/.test(value)) {
        e.target.value = this.editForm.phoneNumber;
        return;
      }

      this.editForm.phoneNumber = value;
      this.phoneError = null;
    },
    async saveProfile() {
      try {
        this.error = null;
        this.phoneError = null;

        // Validate phone number if provided
        let phoneNumber = this.editForm.phoneNumber.trim();
        if (phoneNumber) {
          const validNumber = this.validatePhoneNumber(phoneNumber);
          if (!validNumber) {
            this.phoneError =
              "Please enter a valid phone number (E.164 format: +1234567890)";
            return;
          }
          phoneNumber = validNumber;
        }

        this.loading = true;
        await kappamlAPI.updateProfile({
          display_name: this.editForm.displayName,
          phone_number: phoneNumber || null,
        });

        await this.fetchUserProfile();
        this.isEditing = false;

        if (typeof window !== "undefined" && window.$toast) {
          window.$toast.success("Profile updated successfully");
        }
      } catch (err) {
        this.error = err.message || "Failed to update profile";
        if (typeof window !== "undefined" && window.$toast) {
          window.$toast.error(this.error);
        }
      } finally {
        this.loading = false;
      }
    },
    handleImageError() {
      if (this.$refs.profileImage) {
        this.$refs.profileImage.style.display = "none";
      }
      this.showFallbackAvatar = true;
    },
    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },
  },
};
</script>

<style scoped>
.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.profile-header {
  @apply bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm;
  display: flex;
  align-items: center;
  padding: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  text-align: center;
}

.profile-photo-container {
  margin-right: 0;
  flex-shrink: 0;
  width: 100px;
  height: 100px;
}

.profile-photo {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.fallback-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  @apply bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
}

.profile-title {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
}

.profile-title h1 {
  @apply text-gray-900 dark:text-gray-200;
  font-size: 1.8rem;
  font-weight: bold;
  margin: 0;
}

.email {
  @apply text-gray-600 dark:text-gray-400;
  margin-top: 0.5rem;
}

.info-section {
  @apply bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6;
  margin-bottom: 2rem;
}

.info-section h2 {
  @apply text-gray-900 dark:text-gray-200 font-medium;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.info-grid {
  display: grid;
  gap: 1rem;
}

.info-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  padding: 0.5rem 0;
}

.info-row label {
  @apply text-gray-600 dark:text-gray-400 font-medium;
}

.info-row span {
  @apply text-gray-900 dark:text-gray-200;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading,
.error {
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
}

.error {
  @apply text-red-600 dark:text-red-400;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  @apply text-gray-700 dark:text-gray-300 font-medium;
  display: block;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.form-input {
  @apply w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary;
  font-size: 0.875rem;
  line-height: 1.25rem;
  transition: border-color 0.15s ease-in-out;
}

.button-group {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.btn-primary {
  @apply bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors;
  font-weight: 500;
  border: none;
  cursor: pointer;
}

.btn-secondary {
  @apply bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors;
  font-weight: 500;
  cursor: pointer;
}

.mt-6 {
  margin-top: 1.5rem;
}

.error-input {
  @apply border-red-600 dark:border-red-500;
}

.error-input:focus {
  @apply border-red-600 dark:border-red-500 ring-1 ring-red-600 dark:ring-red-500;
}

.error-text {
  @apply text-red-600 dark:text-red-400;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.help-text {
  @apply text-gray-600 dark:text-gray-400;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

@media (min-width: 640px) {
  .profile-header {
    justify-content: flex-start;
    text-align: left;
  }

  .profile-photo-container {
    margin-right: 2rem;
  }

  .profile-title {
    flex: 1;
    align-items: flex-start;
  }
}
</style>
